/**
 * Müşteri AI sayfası — "her şey tek ekranda" (katalog: KALP, tekoffice.co/app/ai).
 *
 * Aynı v6 sayfası, müşteri hesabında:
 *   · Üstte ortada sekmeler: Document · Template · Assistant.
 *   · Ortada orb, "Good morning, Ahmed.", muhasebecinin beklediği belge sayısı,
 *     altında Camera · Upload · Speak ve hızlı sorular. Konuşma başlayınca orb
 *     küçülür, mesajlar gelir.
 *   · Altta "Ask anything or drop a file…" (dosya sayfaya bırakılabilir).
 *   · Sağda canlı özet: bu ayın geliri, gideri, neti; muhasebeciden gelenler
 *     (bekleyen belge + "Upload now", bu ay eklediği kayıtlar).
 *
 * Sayılar kodla hesaplanır (CU.exp, belge talepleri, my_accountant); AI yok.
 * Ofis hesabında firm-ai düzeni geçerli; bu modül kendi sınıfını kaldırır.
 */

import { defineFeature } from '../../shell/feature.ts';
import { legacy } from '../../shell/bridge.ts';

const g = globalThis as any;
const PLACEHOLDER = 'Ask anything or drop a file…';
const OLD_PLACEHOLDER = 'Ask about your income and expenses…';

interface PendingItem { requestId: string; itemId: string; title: string; label: string; due: string | null }

let observer: MutationObserver | null = null;
let dropInstalled = false;

function el(id: string): HTMLElement | null { return document.getElementById(id); }
function esc(s: unknown): string { return legacy.escapeHtml(String(s ?? '')); }
function isCustomer(): boolean { return g.navSide?.() === 'customer'; }

const CSS = `
#v6.cuai .ai-brand,#v6.cuai #v6-tabbar > button,#v6.cuai .ai-plus,#v6.cuai #ai-conv-btn,
#v6.cuai #ai-mic-sel-btn,#v6.cuai #ai-voice-pick-btn,#v6.cuai #ai-hero-text{display:none!important}
#v6.cuai #v6-tabbar{justify-content:center;border-bottom:none;background:#000;padding:12px 12px 2px}
#v6.cuai #v6-tabbar > div{flex:0 0 auto!important;gap:8px!important;padding:0!important}
#v6.cuai #aip-tab-doc{order:1}#v6.cuai #aip-tab-tpl{order:2}#v6.cuai #aip-tab-asst{order:3}
#v6.cuai #v6-tabbar button[id^="aip-tab-"]{border:1px solid var(--div)!important;border-radius:999px!important;padding:6px 16px!important;font-weight:500!important;background:#1a1a1a!important;color:var(--t2)!important}
#v6.cuai #v6-tabbar button[id^="aip-tab-"][style*="var(--red)"]{background:var(--red)!important;color:#fff!important;border-color:var(--red)!important}
#v6.cuai #v6-chat-panel-wrap{grid-template-columns:minmax(0,1fr) 270px;grid-template-rows:auto minmax(0,1fr) auto;background:#000}
#v6.cuai #ai-hero-wrap{grid-column:1;grid-row:1 / span 2;flex-direction:column;justify-content:center;background:#000;overflow-y:auto;padding:8px 20px}
#v6.cuai #ai-robot-wrap{position:relative!important;inset:auto!important;height:260px!important;width:100%!important;flex-shrink:0}
#v6.cuai #ai-orb-canvas{width:250px!important;max-width:80%}
#v6.cuai #ai-bubble{top:10px}
#v6.cuai #ai-msgs{display:none!important}
#v6.cuai.cuai-chat #ai-hero-wrap{grid-row:1;justify-content:flex-start;padding:0}
#v6.cuai.cuai-chat #ai-robot-wrap{height:130px!important}
#v6.cuai.cuai-chat #ai-orb-canvas{width:120px!important}
#v6.cuai.cuai-chat #cuai-hero{display:none}
#v6.cuai.cuai-chat #ai-msgs{display:block!important;grid-column:1;grid-row:2;background:#000;padding:10px 40px}
#v6.cuai .ai-compose{grid-column:1;grid-row:3;background:#000;padding:10px 60px 18px}
#v6.cuai .ai-inp-row{background:#0f0f0f;border:1px solid var(--div);border-radius:10px;padding:4px 6px 4px 12px}
#v6.cuai #ai-inp{background:none;border:none}
#v6.cuai .ai-send{width:30px;height:30px;background:none;color:var(--red);font-size:14px;box-shadow:none}
#v6.cuai #v6-split.active{grid-column:1;grid-row:1 / span 2}
#v6.cuai.cuai-drop .ai-compose .ai-inp-row{border-color:var(--red)}
#cuai-hero{text-align:center;margin-top:4px}
#cuai-hero h2{font-size:20px;font-weight:700;margin:0 0 4px;color:#fff}
#cuai-hero .sub{font-size:12px;color:var(--t2)}
#cuai-actions{display:flex;justify-content:center;gap:14px;margin:18px 0 14px}
.cuai-act{display:flex;flex-direction:column;align-items:center;gap:6px;font-size:11px;color:var(--t2)}
.cuai-act button{width:62px;height:62px;border-radius:16px;border:1px solid var(--div);background:#1a1a1a;color:#fff;font-size:22px;cursor:pointer;transition:all .15s}
.cuai-act button:hover{background:#242424}
.cuai-act.red button{background:var(--red);border-color:var(--red)}
#v6.cuai #ai-quick-general{justify-content:center;margin:0 auto;max-width:460px}
#cuai-side{grid-column:2;grid-row:1 / span 3;border-left:1px solid var(--div);padding:18px 14px;overflow-y:auto;background:#0b0b0b}
#v6:not(.cuai) #cuai-side{display:none}
.cuai-h{font-size:10px;font-weight:600;letter-spacing:.8px;color:var(--t2);text-transform:uppercase;margin:4px 0 10px}
.cuai-card{border:1px solid var(--div);border-radius:12px;background:#141414;padding:12px 14px;margin-bottom:10px}
.cuai-card .k{font-size:11px;color:var(--t2)}
.cuai-card .v{font-size:19px;font-weight:800;margin-top:4px}
.cuai-msg{border:1px solid var(--div);border-radius:12px;background:#141414;padding:12px 14px;margin-bottom:10px;cursor:default}
.cuai-msg.req{border-color:#b36b00}
.cuai-msg .t{display:flex;align-items:center;gap:8px;font-size:12px;font-weight:700}
.cuai-msg .av{width:22px;height:22px;border-radius:50%;background:#2f63d8;color:#fff;font-size:10px;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.cuai-msg .d{font-size:11px;color:var(--t2);margin:6px 0 0}
@media (max-width:860px){
  #v6.cuai #v6-chat-panel-wrap{grid-template-columns:minmax(0,1fr)}
  #cuai-side{display:none}
  #v6.cuai .ai-compose{padding:8px 16px 14px}
  #v6.cuai.cuai-chat #ai-msgs{padding:10px 16px}
  #v6.cuai #ai-robot-wrap{height:200px!important}
  #v6.cuai #ai-orb-canvas{width:190px!important}
}`;

function installCss(): void {
  if (el('cuai-css')) return;
  const st = document.createElement('style');
  st.id = 'cuai-css';
  st.textContent = CSS;
  document.head.appendChild(st);
}

function greeting(): string {
  const h = new Date().getHours();
  const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
  const name = String(legacy.user?.name ?? '').trim().split(/\s+/)[0];
  return name ? `${part}, ${name}.` : `${part}.`;
}

function money(n: number, cur: string, sign = false): string {
  let s: string;
  try {
    s = new Intl.NumberFormat(undefined, { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 }).format(Math.abs(n));
  } catch {
    s = `${Math.round(Math.abs(n)).toLocaleString()} ${cur}`;
  }
  if (n < 0) return '−' + s;
  return sign && n > 0 ? '+' + s : s;
}

/** Bu ayın geliri/gideri: yalnız ana para birimindeki kayıtlar (karışık birim toplanmaz). */
function monthTotals(): { cur: string; income: number; expense: number; other: boolean } {
  const u = legacy.user as { exp?: Array<{ date: string; type: string; amount: number; cur?: string }>; setup?: { currency?: string } } | null;
  const cur = u?.setup?.currency || 'USD';
  const now = new Date();
  const from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  let income = 0, expense = 0, other = false;
  for (const e of u?.exp ?? []) {
    if (!e || e.date < from) continue;
    if ((e.cur || cur) !== cur) { other = true; continue; }
    if (e.type === 'income') income += Number(e.amount) || 0;
    else expense += Number(e.amount) || 0;
  }
  return { cur, income, expense, other };
}

function buildHero(): void {
  const hero = el('ai-hero-wrap');
  if (!hero || el('cuai-hero')) return;
  const box = document.createElement('div');
  box.id = 'cuai-hero';
  box.innerHTML = `<h2 id="cuai-greet"></h2><div class="sub" id="cuai-sub"></div>`
    + `<div id="cuai-actions">`
    + `<div class="cuai-act"><button type="button" title="Take a photo of a receipt or invoice" onclick="cuaiCamera()">📷</button>Camera</div>`
    + `<div class="cuai-act"><button type="button" title="Upload an invoice, receipt or statement" onclick="document.getElementById('ai-file-inp2').click()">⬆</button>Upload</div>`
    + `<div class="cuai-act red"><button type="button" title="Talk to the assistant" onclick="aiToggleMic()">🎤</button>Speak</div>`
    + `</div>`;
  hero.appendChild(box);
  const quick = el('ai-quick-general');
  if (quick) box.appendChild(quick);
}

async function renderHero(): Promise<PendingItem[]> {
  const greet = el('cuai-greet');
  const sub = el('cuai-sub');
  if (greet) greet.textContent = greeting();
  let items: PendingItem[] = [];
  try { items = (await g.drPendingItems?.()) ?? []; } catch { items = []; }
  const n = items.length;
  if (sub) sub.textContent = n
    ? `Your accountant needs ${n} document${n === 1 ? '' : 's'}. Tap the orb or just talk.`
    : 'Tap the orb or just talk.';
  return items;
}

async function renderSide(items: PendingItem[]): Promise<void> {
  const wrap = el('v6-chat-panel-wrap');
  if (!wrap) return;
  let side = el('cuai-side');
  if (!side) {
    side = document.createElement('aside');
    side.id = 'cuai-side';
    wrap.appendChild(side);
  }
  const t = monthTotals();
  const net = t.income - t.expense;
  const month = new Date().toLocaleDateString('en', { month: 'long' });

  let acc: any = null;
  try { acc = (await legacy.sb?.rpc('my_accountant'))?.data ?? null; } catch { acc = null; }
  const firm = acc?.linked ? String(acc.firm_name ?? '') : '';
  const initial = esc((firm || '?').charAt(0).toUpperCase());
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
  const added = (acc?.changes ?? []).filter((c: any) => new Date(c.written_at).getTime() >= monthStart).length;

  const req = items[0];
  const msgs = (req
    ? `<div class="cuai-msg req"><div class="t"><span class="av">${initial}</span>Document request</div>`
      + `<div class="d">${esc(req.label)}${items.length > 1 ? ` · +${items.length - 1} more` : ''}</div>`
      + `<button class="btn br bsm" style="margin-top:10px" onclick="drUpload('${esc(req.requestId)}','${esc(req.itemId)}',this)">Upload now</button></div>`
    : '')
    + (added
      ? `<div class="cuai-msg" style="cursor:pointer" onclick="go('v10','ni10')"><div class="t"><span class="av">${initial}</span>${added} ${added === 1 ? 'entry' : 'entries'} added</div>`
        + `<div class="d">by ${esc(firm)} this month</div></div>`
      : '');

  side.innerHTML = `<div class="cuai-h">${esc(month)} · so far</div>`
    + `<div class="cuai-card"><div class="k">Income</div><div class="v" style="color:var(--grn)" translate="no">${esc(money(t.income, t.cur))}</div></div>`
    + `<div class="cuai-card"><div class="k">Expense</div><div class="v" style="color:var(--red)" translate="no">${esc(money(t.expense, t.cur))}</div></div>`
    + `<div class="cuai-card"><div class="k">Net</div><div class="v" style="color:#fff" translate="no">${esc(money(net, t.cur, true))}</div>`
    + (t.other ? `<div class="k" style="margin-top:4px">Other currencies not included</div>` : '') + `</div>`
    + (firm || msgs
      ? `<div class="cuai-h" style="margin-top:18px">From your accountant</div>`
        + (msgs || `<div style="font-size:11px;color:var(--t2)">Nothing new from ${esc(firm)}.</div>`)
      : '');
}

function watchChat(): void {
  const msgs = el('ai-msgs');
  const v6 = el('v6');
  if (!msgs || !v6) return;
  const sync = () => v6.classList.toggle('cuai-chat', v6.classList.contains('cuai') && !!msgs.querySelector('.ai-msg'));
  sync();
  if (!observer) {
    observer = new MutationObserver(sync);
    observer.observe(msgs, { childList: true });
  }
}

function installDrop(): void {
  const v6 = el('v6');
  if (!v6 || dropInstalled) return;
  dropInstalled = true;
  const active = () => v6.classList.contains('cuai');
  v6.addEventListener('dragover', (e) => { if (!active()) return; e.preventDefault(); v6.classList.add('cuai-drop'); });
  v6.addEventListener('dragleave', () => v6.classList.remove('cuai-drop'));
  v6.addEventListener('drop', (e) => {
    if (!active()) return;
    e.preventDefault();
    v6.classList.remove('cuai-drop');
    const f = e.dataTransfer?.files?.[0];
    if (f) g.aipReadFinancial?.(f);
  });
}

function apply(): void {
  const v6 = el('v6');
  if (!v6) return;
  const on = isCustomer();
  installCss();
  v6.classList.toggle('cuai', on);
  if (!on) { v6.classList.remove('cuai-chat'); return; }
  const inp = el('ai-inp') as HTMLInputElement | null;
  if (inp && (inp.placeholder === OLD_PLACEHOLDER || !inp.placeholder)) inp.placeholder = PLACEHOLDER;
  buildHero();
  watchChat();
  installDrop();
  void renderHero().then((items) => renderSide(items));
}

export default defineFeature({
  id: 'customer-ai',
  label: 'Müşteri AI sayfası (tek ekran)',
  /* v6'nın sahibi doc-requests (kayıt tek modül izin verir); onun mount'u cuaiApply'ı çağırır. */
  view: null,
  legacyGlobals: {
    cuaiApply: () => apply(),
    cuaiCamera: () => {
      const inp = document.createElement('input');
      inp.type = 'file';
      inp.accept = 'image/*';
      inp.setAttribute('capture', 'environment');
      inp.onchange = () => { const f = inp.files && inp.files[0]; if (f) g.aipReadFinancial?.(f); };
      inp.click();
    },
  },
});
